import { Controller } from "@hotwired/stimulus"

// Shows a "thinking" state on a form's submit button while a slow server-side
// action runs (e.g. generating the agent recommendation, which takes a few
// seconds). Locks the button and form fields so the request can't be fired
// twice and so it's obvious we're working.
//
// Usage:
//   <form data-controller="submit-loading"
//         data-action="submit->submit-loading#start">
//     <textarea ...></textarea>
//     <button data-submit-loading-target="button"
//             data-submit-loading-text-value="Designing your agent...">Go</button>
//   </form>
export default class extends Controller {
  static targets = ["button"]
  static values = { text: String }

  connect() {
    // If the page is restored from the back/forward cache, the button may still
    // be in its locked state from the previous navigation. Reset it.
    this.onPageShow = (event) => { if (event.persisted) this.reset() }
    window.addEventListener("pageshow", this.onPageShow)
  }

  disconnect() {
    window.removeEventListener("pageshow", this.onPageShow)
  }

  start() {
    const button = this.hasButtonTarget ? this.buttonTarget : null
    if (!button || button.dataset.loading === "true") return

    button.dataset.loading = "true"
    button.dataset.originalHtml = button.innerHTML

    const spinner =
      '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>'
    const label = this.textValue
      ? `<span class="ms-2">${this.textValue}</span>`
      : ""
    button.innerHTML = `${spinner}${label}`
    button.disabled = true
    button.setAttribute("aria-busy", "true")

    // Lock text inputs visually. Use readOnly (not disabled) so their values are
    // still included when the form submits.
    this.element
      .querySelectorAll("input[type=text], textarea")
      .forEach((field) => {
        field.readOnly = true
        field.classList.add("bg-light")
      })
  }

  reset() {
    const button = this.hasButtonTarget ? this.buttonTarget : null
    if (button && button.dataset.loading === "true") {
      button.innerHTML = button.dataset.originalHtml
      button.disabled = false
      button.removeAttribute("aria-busy")
      delete button.dataset.loading
      delete button.dataset.originalHtml
    }
    this.element
      .querySelectorAll("input[type=text], textarea")
      .forEach((field) => {
        field.readOnly = false
        field.classList.remove("bg-light")
      })
  }
}
